/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import _isEqual from 'lodash/isEqual';
import moment from 'moment-timezone';
import { utcDateOrUndefined } from 'volta/utils/date-utils';

import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { IDateFilter } from 'volta/_pods/protected/bom/controller';
import type { BvLabelledSignature } from './../bv-labelled/index';
import type { BvDateFieldSignature } from '../bv-date-field';
import type { NormalizeRangeActionValue } from 'ember-power-calendar/utils';
import type { TDate } from 'volta/models/types/calendar';
import type { BvPopoverSignature } from '../bv-popover';

interface BvDateRangeFieldSignature {
  Args: Pick<BvLabelledSignature['Args'], 'label' | 'labelHidden'> &
    Pick<BvDateFieldSignature['Args'], 'allowClear'> & {
      dateFilter?: IDateFilter;
      onChange?: (filter: IDateFilter) => void;
      inlineCalendar?: boolean;
      minStartDate?: TDate;
      startDatePlaceholder?: string;
      endDatePlaceholder?: string;
      calendarSize?: 'sm' | 'md' | 'lg';
      horizontalPosition?: BvPopoverSignature['Args']['horizontalPosition'];
    };
  Element: HTMLDivElement;
}

export default class BvDateRangeFieldComponent extends Component<BvDateRangeFieldSignature> {
  @tracked selectedStartDate?: TDate = this.args.dateFilter?.startDate;
  @tracked selectedEndDate?: TDate = this.args.dateFilter?.endDate;
  @tracked calCenter: Date = moment.utc(this.selectedStartDate ?? this.selectedEndDate).toDate();
  @tracked focusedOn?: 'start' | 'end';

  get selection() {
    return {
      startDate: utcDateOrUndefined(this.selectedStartDate),
      endDate: utcDateOrUndefined(this.selectedEndDate)
    };
  }

  get selectedRange() {
    const { startDate, endDate } = this.selection;
    return {
      start: startDate,
      end: endDate
    };
  }

  @action
  handleCenterChange(center: { date: Date }) {
    if (center.date) {
      this.calCenter = moment.utc(center.date).toDate();
    }
  }

  @action
  handlePopoverOpen() {
    this.calCenter = moment.utc(this.selectedStartDate ?? this.selectedEndDate).toDate();
  }

  @action
  handleStartDateChange(date?: TDate) {
    this.selectedStartDate = utcDateOrUndefined(date);
    if (date) {
      this.calCenter = moment.utc(date).toDate();
    }

    this.args.onChange?.(this.selection);
  }

  @action
  onCalendarSelect(days: NormalizeRangeActionValue) {
    const { start, end } = days.date;
    // When the end date has the focus and no start date is defined
    // It seems better to give the value to the end date
    // It might not be the case in all situation
    const newEndDate = end ?? start;
    if (
      this.focusedOn === 'end' &&
      (!this.selectedStartDate || moment.utc(newEndDate).isAfter(this.selectedStartDate))
    ) {
      this.selectedEndDate = utcDateOrUndefined(newEndDate);
    } else {
      this.selectedStartDate = utcDateOrUndefined(start);
      this.selectedEndDate = utcDateOrUndefined(end);

      if (start && !end) {
        this.focusedOn = 'end';
      }
    }
    this.args.onChange?.(this.selection);
  }

  @action
  handleEndDateChange(date?: TDate) {
    this.selectedEndDate = utcDateOrUndefined(date);
    if (date) {
      this.calCenter = moment.utc(date).toDate();
    }
    this.args.onChange?.(this.selection);
  }

  /**
   * This handler reset the start and end date values when the dateFilter is updated externally
   */
  @action
  handleValueChange(_el: HTMLElement, [value]: [IDateFilter | undefined]) {
    const { selection } = this;
    if (value && !_isEqual(selection, value)) {
      this.selectedStartDate = utcDateOrUndefined(value.startDate);
      this.selectedEndDate = utcDateOrUndefined(value.endDate);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvDateRangeField: typeof BvDateRangeFieldComponent;
    'bv-date-range-field': typeof BvDateRangeFieldComponent;
  }
}
